/** @jsxImportSource ./simboluri-jsx */
import { ReactNode } from "react";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import HoverCard from "./HoverCard";
import { CatalogVerificari, CategorieVerificare as Categorie, categoriaDin, tipDinNume } from "./tipuri";
import TextSimboluri from "./Simbol";

// TITLUL unei verificări — ACELAȘI pe toate paginile (Pașii 6, 10, 13, 14, 15
// și tabelul Pasului 19), cu aceeași explicație la hover și același stil.
//
// Înainte, fiecare pagină scria numele emis de pas („νd ≤ 0.45"), iar Pasul 19
// scria cheia tehnică („nu_d"): două nume diferite pentru aceeași verificare,
// niciunul lizibil. Acum ambele pagini cer catalogul de la server
// (`catalog_verificari.py`) și afișează:
//
//     Stâlp: efortul axial normalizat ν_d …        [PASUL 15]   νd ≤ 0.45
//     └ titlul din catalog (hover = explicația)     └ pasul      └ numele emis
//
// La hover: unde se calculează, ce verifică, formula CU sursa ei (foaia
// structuristului → legislația din România → Eurocod, în ordinea de
// prioritate a casei), fiecare simbol explicat, ce problemă ascunde, unde
// implementarea diferă de normativul românesc, și soluțiile ordonate după
// cost/efect.

import { useCatalogVerificari } from "./useCatalog";
export { useCatalogVerificari };

const ETICHETA_SURSA: Record<string, Cheie> = {
  foaie: "struct.ver.sursa_foaie",
  ro: "struct.ver.sursa_ro",
  ec: "struct.ver.sursa_ec",
};

/** Explicația COMPLETĂ a unei categorii — tot ce știe catalogul despre ea.
 *  Se citește doar la cerere (hover pe titlu, sau butonul
 *  „explicația completă" din detaliul verificării): în pagină rămâne o
 *  singură propoziție, `cat.scurt`. Motivul, în cuvintele lui Stefan: „la
 *  validări e mult blabla […] teoria poate să fie doar dacă dai hover pe
 *  titlu, că o știe deja structuristul." */
export function ExplicatiaCompleta({ tip, cat }: { tip: string; cat: Categorie }) {
  return <Continut tip={tip} cat={cat} />;
}

function Continut({ tip, cat }: { tip: string; cat: Categorie }) {
  const t = useT();
  return (
    <>
      <span className="fx-tip-title">{cat.eticheta}</span>
      {/* «Unde se calculează: Pasul N — …» nu se arată: pașii
          sunt vocabularul NOSTRU, nu al cititorului („nu îl
          interesează pe user de pașii noștri"). Cheia tehnică rămâne la
          îndemână ca `title`, pentru când o căutăm în cod. */}
      <span className="fx-tip-block" title={tip}>
        <span className="fx-tip-label">{t("struct.ver.ce_verifica")}</span>
        <span>{cat.ce}</span>
      </span>
      <span className="fx-tip-block">
        <span className="fx-tip-label">
          {t("struct.ver.formula")}
          {cat.sursa_formula && (
            <span className={`cv-sursa-badge cv-sursa-${cat.sursa_formula}`}>
              {ETICHETA_SURSA[cat.sursa_formula]
                ? t(ETICHETA_SURSA[cat.sursa_formula])
                : cat.sursa_formula}
            </span>
          )}
        </span>
        <span className="cv-formula"><TextSimboluri text={cat.formula} locale={cat.simboluri} /></span>
        <span className="cv-articol">{cat.articol}</span>
      </span>
      {cat.simboluri?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">{t("struct.ver.simboluri")}</span>
          <span className="fx-params">
            {cat.simboluri.map((s) => (
              <span key={s.simbol} className="fx-param">
                <code>{s.simbol}</code>
                <span>
                  <strong><TextSimboluri text={s.nume} locale={cat.simboluri} /></strong>
                  <span className="cv-de-unde">{t("struct.info.de_unde_prefix")}<TextSimboluri text={s.de_unde} locale={cat.simboluri} /></span>
                </span>
              </span>
            ))}
          </span>
        </span>
      )}
      {cat.divergenta && (
        <span className="fx-tip-block cv-divergenta">
          <span className="fx-tip-label">
            {t("struct.ver.divergenta", { fata_de: cat.divergenta.fata_de })}
          </span>
          <span>
            <strong>{t("struct.ver.implementat")}</strong> {cat.divergenta.implementat}
            <br />
            <strong>{t("struct.ver.normativul_cere")}</strong> {cat.divergenta.normativ}
            <br />
            <strong>{t("struct.ver.efectul")}</strong> {cat.divergenta.efect}
          </span>
        </span>
      )}
      <span className="fx-tip-block">
        <span className="fx-tip-label">{t("struct.ver.ce_ascunde")}</span>
        <span>{cat.cand}</span>
      </span>
      {cat.solutii?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">
            {t("struct.ver.solutii")}
          </span>
          <span className="cv-solutii">
            {cat.solutii.map((s, i) => (
              <span key={i} className="cv-solutie">
                <span className="cv-nr">{i + 1}</span>
                <span>
                  <strong>{s.ce}</strong> — {s.efect}
                  <span className="cv-cost">{s.cost}</span>
                  {s.automat && (
                    <span className="cv-automat">
                      {t("struct.ver.automat", { cum: s.automat })}
                    </span>
                  )}
                  {s.sursa && <span className="cv-sursa">{s.sursa}</span>}
                </span>
              </span>
            ))}
          </span>
        </span>
      )}
    </>
  );
}

/** Titlul unei verificări, cu explicația la hover. Se dă FIE `tip` (tabelul
 *  Pasului 19), FIE pasul + numele emis (paginile pașilor). */
export default function TitluVerificare({
  catalog,
  tip,
  pas,
  nume,
  grupa,
  id,
  aratNumele = true,
  aratPasul = true,
  icoana = false,
  text,
}: {
  catalog: CatalogVerificari | null;
  tip?: string;
  pas?: string;
  nume?: string;
  grupa?: string;
  id?: string;
  aratNumele?: boolean;   // numele emis de pas, sub titlu
  aratPasul?: boolean;    // chip-ul „PASUL N" (redundant în pagina pasului)
  // Declanșatorul explicației: implicit e TITLUL. Cu `icoana`, titlul rămâne
  // text simplu și explicația atârnă de un ⓘ de lângă el — acolo unde titlul
  // e deja butonul altcuiva (capul cardului din tabloul de verificări, care
  // se deschide la clic). Stefan: „da, mai bine pe un i".
  icoana?: boolean;
  // textul VIZIBIL, când titlul complet ar umple un tabel cu zeci de rânduri
  // identice (ex.: coloana «grupare» din Pasul 13). Explicația la hover
  // rămâne exact aceeași — se schimbă doar cât se vede fără hover.
  text?: string;
}) {
  const t = useT();
  const tipFinal = tip ?? tipDinNume(catalog, pas, nume, grupa, id) ?? "";
  const cat = categoriaDin(catalog, tipFinal);

  if (!cat)
    // catalogul n-a ajuns încă, sau categoria e nouă: numele emis de pas e
    // tot ce avem — mai bine el decât un rând gol
    return <span>{text ?? nume ?? tipFinal}</span>;

  const cartonas = (copil: ReactNode) => (
    <HoverCard
      latime={520}
      clasaCard="cv-tip"
      eticheta={t("struct.info.explicatie_pentru", { ce: cat.eticheta })}
      continut={<Continut tip={tipFinal} cat={cat} />}
    >
      {copil}
    </HoverCard>
  );

  return (
    <span className="cv-titlu">
      {icoana ? (
        <>
          <span className="cv-nume-simplu">{text ?? cat.eticheta}</span>
          {cartonas(<span className="cv-i" aria-hidden="true">i</span>)}
        </>
      ) : (
        cartonas(<span className="cv-nume">{text ?? cat.eticheta}</span>)
      )}
      {aratPasul && <span className="cv-pas">{t("struct.pas.scurt", { nr: cat.pas })}</span>}
      {cat.divergenta && (
        <span className="cv-div-marker" title={t("struct.ver.marker_metoda_titlu")}>
          {t("struct.ver.marker_metoda")}
        </span>
      )}
      {aratNumele && nume && <span className="cv-nume-emis">{nume}</span>}
    </span>
  );
}
